import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { listingsApi } from '../api/listings';
import { LISTING_STATES } from '../types';
import StateBadge from '../components/StateBadge';
import SortTh from '../components/SortTh';
import InserateFilterBar from '../components/InserateFilterBar';
import Spinner from '../components/Spinner';
import EmptyTableRow from '../components/EmptyTableRow';
import Pagination from '../components/Pagination';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import AddressStatusDot from '../components/AddressStatusDot';
import OnlineStatusDot from '../components/OnlineStatusDot';
import { useInserateFilter } from '../hooks/useInserateFilter';
import { useSessionFilter } from '../hooks/useSessionFilter';
import { useCan } from '../hooks/useCan';
import { useColumnConfig, useModuleVisibility } from '../hooks/useConfigVisibility';
import { restoreOne } from '../api/softDelete';

export default function InserateList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // ── Filter-State (vollständig im Hook) ──────────────────────────────────────
  const filterHook = useInserateFilter({
    storageKey: 'inserate_filter',
    searchParams,
  });

  // ── Pagination + Sortierung (seitenspezifisch) ───────────────────────────────
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useSessionFilter<string>('inserate_sortBy', 'last_updated_at');
  const [sortDir, setSortDir] = useSessionFilter<'asc' | 'desc'>('inserate_sortDir', 'desc');

  // ── Bulk-Selection ────────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkState, setBulkState] = useState('');
  const [bulkComment, setBulkComment] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ── Hidden-Toggle (city_admin/system_admin sehen alle, member nur eigene) ───
  const canRestoreHidden = useCan('restore_hidden');
  const canRestoreOwn = useCan('restore_own');
  const [showHidden, setShowHidden] = useState(false);
  const cols = useColumnConfig('inserate');
  // module_visibility overrides column_config: a link column to a disabled module
  // is suppressed regardless of the column toggle.
  const pv = useModuleVisibility();

  // ── API-Queries ──────────────────────────────────────────────────────────────
  const queryParams = {
    ...filterHook.filterParams,
    page,
    per_page: 50,
    sort_by: sortBy,
    sort_dir: sortDir,
    include_hidden: showHidden,
  };

  const { data: listingsData, isLoading } = useQuery({
    queryKey: ['listings', queryParams],
    queryFn: () => listingsApi.getListings(queryParams),
  });

  const { data: stats } = useQuery({
    queryKey: ['listings', 'stats'],
    queryFn: listingsApi.getStats,
  });

  const { data: platformOptions = [] } = useQuery({
    queryKey: ['listings', 'platforms'],
    queryFn: listingsApi.getPlatforms,
  });

  const { data: listingTypeOptions = [] } = useQuery({
    queryKey: ['listings', 'listing-types'],
    queryFn: listingsApi.getListingTypes,
  });

  // ── Mutations ─────────────────────────────────────────────────────────────────
  const bulkStateMutation = useMutation({
    mutationFn: () =>
      listingsApi.bulkChangeState([...selectedIds], bulkState, bulkComment || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      setSelectedIds(new Set());
      setShowBulkActions(false);
      setBulkState('');
      setBulkComment('');
    },
  });

  const bulkScrapeMutation = useMutation({
    mutationFn: () => listingsApi.bulkScrapeDetails([...selectedIds]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      setSelectedIds(new Set());
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (id: number) => restoreOne('inserat', id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
    },
  });

  // ── Hilfs-Handler ─────────────────────────────────────────────────────────────
  const toggleSelection = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!listingsData) return;
    const allIds = listingsData.items.map(l => l.id);
    const allSelected = allIds.every(id => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(allIds));
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setPage(1);
  };

  const totalPages = listingsData ? Math.ceil(listingsData.total / 50) : 0;

  // ── Render ─────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-6">

        {/* Filter-Leiste (vollständig geteilt mit Karte) */}
        <InserateFilterBar
          {...filterHook}
          stats={stats}
          platformOptions={platformOptions}
          listingTypeOptions={listingTypeOptions}
          onAnyChange={() => setPage(1)}
        />

        {(canRestoreHidden || canRestoreOwn) && (
          <div className="flex items-center gap-2 mb-3">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={showHidden}
                onChange={(e) => {
                  setShowHidden(e.target.checked);
                  setPage(1);
                  setSelectedIds(new Set());
                }}
              />
              Ausgeblendete anzeigen
            </label>
          </div>
        )}

        {/* Bulk-Aktionen-Leiste */}
        {selectedIds.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center justify-between">
            <span className="text-sm font-medium text-blue-800">
              {selectedIds.size} Inserat{selectedIds.size !== 1 ? 'e' : ''} ausgewählt
            </span>
            <div className="flex items-center gap-2">
              {!showBulkActions ? (
                <>
                  <button
                    onClick={() => setShowBulkActions(true)}
                    className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700"
                  >
                    Status ändern
                  </button>
                  <button
                    onClick={() => bulkScrapeMutation.mutate()}
                    disabled={bulkScrapeMutation.isPending}
                    className="bg-green-600 text-white px-3 py-1.5 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                  >
                    {bulkScrapeMutation.isPending ? 'Wird gestartet…' : 'Aktualisieren'}
                  </button>
                  <button
                    onClick={() => setConfirmOpen(true)}
                    className="bg-red-600 text-white px-3 py-1.5 rounded text-sm hover:bg-red-700"
                  >
                    Ausblenden
                  </button>
                  <button
                    onClick={() => setSelectedIds(new Set())}
                    className="text-gray-600 hover:text-gray-800 text-sm"
                  >
                    Auswahl aufheben
                  </button>
                </>
              ) : (
                <>
                  <select
                    value={bulkState}
                    onChange={e => setBulkState(e.target.value)}
                    className="border rounded px-2 py-1.5 text-sm"
                  >
                    <option value="">Status wählen...</option>
                    {Object.entries(LISTING_STATES).map(([key, info]) => (
                      <option key={key} value={key}>
                        {info.label}
                      </option>
                    ))}
                  </select>
                  <input
                    value={bulkComment}
                    onChange={e => setBulkComment(e.target.value)}
                    placeholder="Kommentar (optional)"
                    className="border rounded px-2 py-1.5 text-sm w-48"
                  />
                  <button
                    onClick={() => bulkStateMutation.mutate()}
                    disabled={!bulkState || bulkStateMutation.isPending}
                    className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    {bulkStateMutation.isPending ? '...' : 'Anwenden'}
                  </button>
                  <button
                    onClick={() => {
                      setShowBulkActions(false);
                      setBulkState('');
                      setBulkComment('');
                    }}
                    className="text-gray-600 hover:text-gray-800 text-sm"
                  >
                    Abbrechen
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Tabelle */}
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={
                          !!listingsData?.items?.length &&
                          listingsData.items.every(l => selectedIds.has(l.id))
                        }
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <SortTh field="title" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>
                      Titel
                    </SortTh>
                    {cols.address !== false && (
                      <SortTh field="address" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>
                        Adresse
                      </SortTh>
                    )}
                    {cols.zipcode !== false && (
                      <SortTh field="zipcode" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>
                        PLZ
                      </SortTh>
                    )}
                    {cols.platform !== false && (
                      <SortTh field="platform" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>
                        Plattform
                      </SortTh>
                    )}
                    {cols.state !== false && (
                      <SortTh field="state" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>
                        Status
                      </SortTh>
                    )}
                    {cols.vermieter !== false && pv.vermieter !== false && (
                      <SortTh
                        field="vermieter_name"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      >
                        Vermieter
                      </SortTh>
                    )}
                    {cols.verfahren !== false && pv.verfahren !== false && (
                      <SortTh
                        field="verfahren_aktenzeichen"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      >
                        Verfahren
                      </SortTh>
                    )}
                    <SortTh
                      field="last_updated_at"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onSort={handleSort}
                    >
                      Geändert
                    </SortTh>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {listingsData?.items?.map(listing => (
                    <tr
                      key={listing.id}
                      className={`hover:bg-gray-50 cursor-pointer ${
                        selectedIds.has(listing.id) ? 'bg-blue-50' : ''
                      } ${listing.deleted_at ? 'bg-slate-100 opacity-60' : ''}`}
                      onClick={() => navigate(`/inserat/${listing.id}`)}
                    >
                      <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(listing.id)}
                          onChange={() => toggleSelection(listing.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-3 py-3 text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                          <OnlineStatusDot
                            isOnline={listing.is_online}
                            offlineSince={listing.offline_since}
                          />
                          <span className="font-medium text-gray-900 truncate max-w-xs">
                            {listing.title}
                          </span>
                        </div>
                        {listing.listing_type && (
                          <div className="text-xs text-gray-500 ml-4">{listing.listing_type}</div>
                        )}
                      </td>
                      {cols.address !== false && (
                        <td className="px-3 py-3 text-sm text-gray-500 max-w-xs">
                          <div className="flex items-center gap-2 min-w-0">
                            <AddressStatusDot
                              address={listing.address}
                              verified={listing.address_verified}
                            />
                            <span className="truncate">
                              {(listing.address && listing.zipcode
                                ? listing.address
                                    .replace(listing.zipcode, '')
                                    .replace(/,\s*,/g, ',')
                                    .replace(/,?\s*$/, '')
                                    .trim()
                                : listing.address || listing.neighborhood) || '-'}
                            </span>
                          </div>
                        </td>
                      )}
                      {cols.zipcode !== false && (
                        <td className="px-3 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {listing.zipcode || '-'}
                        </td>
                      )}
                      {cols.platform !== false && (
                        <td className="px-3 py-3 text-sm text-gray-500 capitalize whitespace-nowrap">
                          {listing.resolved_platform || listing.platform}
                        </td>
                      )}
                      {cols.state !== false && (
                        <td className="px-3 py-3 whitespace-nowrap">
                          <StateBadge state={listing.state} />
                        </td>
                      )}
                      {cols.vermieter !== false && pv.vermieter !== false && (
                        <td className="px-3 py-3 text-sm">
                          {listing.vermieter_name ? (
                            <button
                              className="text-blue-600 hover:underline"
                              onClick={e => {
                                e.stopPropagation();
                                navigate(`/vermieter/${listing.vermieter_id}`);
                              }}
                            >
                              {listing.vermieter_name}
                            </button>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      )}
                      {cols.verfahren !== false && pv.verfahren !== false && (
                        <td className="px-3 py-3 text-sm">
                          {listing.verfahren_aktenzeichen ? (
                            <button
                              className="text-blue-600 hover:underline"
                              onClick={e => {
                                e.stopPropagation();
                                navigate(`/verfahren/${listing.verfahren_id}`);
                              }}
                            >
                              {listing.verfahren_aktenzeichen}
                            </button>
                          ) : listing.verfahren_id ? (
                            <button
                              className="text-blue-600 hover:underline"
                              onClick={e => {
                                e.stopPropagation();
                                navigate(`/verfahren/${listing.verfahren_id}`);
                              }}
                            >
                              #{listing.verfahren_id}
                            </button>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      )}
                      <td className="px-3 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {listing.deleted_at ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              restoreMutation.mutate(listing.id);
                            }}
                            className="text-green-700 hover:text-green-900 text-sm font-medium"
                          >
                            Wiederherstellen
                          </button>
                        ) : (
                          new Date(
                            listing.last_updated_at || listing.first_scraped_at,
                          ).toLocaleDateString('de-DE')
                        )}
                      </td>
                    </tr>
                  ))}
                  {(!listingsData?.items || listingsData.items.length === 0) && (
                    <EmptyTableRow colSpan={9} message="Keine Inserate gefunden." />
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              totalPages={totalPages}
              total={listingsData?.total ?? 0}
              entityLabel="Inserate"
              onPageChange={setPage}
            />
          </>
        )}

        <ConfirmDeleteModal
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          entity="inserat"
          ids={[...selectedIds]}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['listings'] });
            setSelectedIds(new Set());
            setConfirmOpen(false);
          }}
        />
      </main>
    </div>
  );
}
